import { Injectable } from '@angular/core';
import { LoaderOptions, WorkflowLoaderEvent, WorkflowLoaderSegmentInfo } from '@shared/model/workflow-loader.model';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WorkflowLoaderService {
  #loaderSubject = new BehaviorSubject<WorkflowLoaderEvent>({ type: 'init', options: { animationId: 'none' } });
  #initialState?: WorkflowLoaderEvent = { type: 'init', options: { animationId: 'none' } };
  #segmentData: WorkflowLoaderSegmentInfo;

  constructor() {
    this.#segmentData = { hasSegments: false };
  }

  /** An observable to listen to loader changes. */
  public loaderState$ = this.#loaderSubject.asObservable();
  /** The state called in start, or default if last event called was clear. */
  public get initial(): WorkflowLoaderEvent {
    return this.#initialState;
  }
  /** Get the last known value sent to the loader. */
  public get state(): WorkflowLoaderEvent {
    return this.#loaderSubject.value;
  }
  /** The segment information used to track segments in the loader - needs to be persisted between instances. */
  public get segmentInfo(): WorkflowLoaderSegmentInfo {
    return this.#segmentData;
  }

  public start(message?: string, options?: LoaderOptions): void {
    this.#segmentData = {
      hasSegments: options?.segments > 0,
      totalSegments: options?.segments,
      activeSegment: 0
    };
    this.#loaderSubject.next({ type: 'start', message, options });
    this.#initialState = this.state;
  }

  public next(message?: string, options?: LoaderOptions): void {
    if (this.#segmentData.hasSegments && this.segmentInfo.activeSegment < this.segmentInfo.totalSegments) this.#segmentData.activeSegment++;
    this.#loaderSubject.next({ type: 'next', message, options });
  }

  public end(message?: string, options?: LoaderOptions): void {
    if (this.#segmentData.hasSegments) this.#segmentData.activeSegment = this.#segmentData.totalSegments;
    this.#loaderSubject.next({ type: 'end', message, options });
  }

  public show(): void {
    // Implement the show method
    this.#loaderSubject.next({ type: 'show' });
  }

  public hide(): void {
    // Implement the hide method
    this.#loaderSubject.next({ type: 'hide' });
  }

  public clear(): void {
    this.#segmentData = { hasSegments: false };
    this.#initialState = this.state;
    this.#loaderSubject.next({ type: 'clear' });
  }
}
